import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import AchievementCard from './AchievementCard';
import achievementService from '../../services/achievementService';
import userService from '../../services/userService';
import { getGamesConfig } from '../../config/games';
import './AchievementsPage.scss';

const PublicAchievementsPage = () => {
  const { username } = useParams();
  const [publicUser, setPublicUser] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Define game types to be grouped under "Total Wins" - dynamically derived from config
  const gameTypes = getGamesConfig().map(game => game.id);

  const fetchPublicProfile = useCallback(async () => {
    try {
      const profileData = await userService.getPublicProfile(username);
      setPublicUser(profileData);
      return profileData;
    } catch (error) {
      console.error('Error fetching public profile:', error);
      if (error.message && error.message.includes('404')) {
        setError('User not found');
      } else {
        setError('Failed to load profile');
      }
      return null;
    }
  }, [username]);

  const fetchPublicAchievements = useCallback(async (userId) => {
    try {
      const data = await achievementService.getPublicUserAchievementsById(userId);
      setAchievements(data.allAchievements || []);
    } catch (error) {
      console.error('Error fetching public achievements:', error);
      setError('Failed to load achievements');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      
      const profileData = await fetchPublicProfile();
      if (profileData) {
        await fetchPublicAchievements(profileData.id);
      }
      
      setIsLoading(false);
    };

    if (username) {
      loadData();
    }
  }, [username, fetchPublicProfile, fetchPublicAchievements]);

  // Filter achievements based on selected filter
  const filterAchievements = (achievementList) => {
    switch (filter) {
      case 'unlocked':
        return achievementList.filter(a => a.unlocked);
      case 'locked':
        return achievementList.filter(a => !a.unlocked);
      case 'special':
        return achievementList.filter(a => a.category === 'user-groups');
      case 'daily-streak':
        return achievementList.filter(a => a.category === 'daily-streak');
      case 'total-wins':
        return achievementList.filter(a => gameTypes.includes(a.game_type));
      default:
        return achievementList;
    }
  };

  const getPublicProfileImageUrl = () => {
    if (publicUser?.profile_picture) {
      return `/api/profile-picture/${publicUser.profile_picture}`;
    }
    return '/unset-profile-pic.png';
  };

  const unlockedCount = achievements.filter(a => a.unlocked).length;

  if (isLoading) {
    return (
      <div className="achievements-page">
        <div className="achievements-page__container">
          <div className="achievements-page__loading">Loading achievements...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="achievements-page">
        <div className="achievements-page__container">
          <div className="achievements-page__error">
            <h2>{error}</h2>
            <p>
              {error === 'User not found' 
                ? `The user "${username}" could not be found.`
                : 'There was an error loading this user\'s achievements. Please try again later.'
              }
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!publicUser) {
    return (
      <div className="achievements-page">
        <div className="achievements-page__container">
          <div className="achievements-page__error">
            <h2>User not found</h2>
            <p>The requested user could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'unlocked', label: 'Unlocked' },
    { value: 'locked', label: 'Locked' },
    { value: 'special', label: 'Special' },
    { value: 'daily-streak', label: 'Daily Streak' },
    { value: 'total-wins', label: 'Total Wins' },
  ];

  return (
    <div className="achievements-page">
      <div className="achievements-page__container">
        <div className="achievements-page__header">
          <h1>Achievements</h1>
          <p>Track your progress and unlock rewards as you play</p>
        </div>

        {/* User Context Banner */}
        <div className="achievements-page__user-banner">
          <div className="achievements-page__user-info">
            <div className="achievements-page__user-avatar">
              <img
                src={getPublicProfileImageUrl()}
                alt={`${publicUser.username}'s profile`}
                className="achievements-page__user-avatar-image"
                onError={(e) => {
                  e.target.src = '/unset-profile-pic.png';
                }}
              />
            </div>
            <div className="achievements-page__user-details">
              <h2 className="achievements-page__username">{publicUser.username}</h2>
              <p className="achievements-page__user-joined">
                Member since {new Date(publicUser.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div className="achievements-page__stats">
            <div className="achievements-page__stat achievements-page__stat--single">
              <span className="achievements-page__stat-number">{unlockedCount}</span>
              <span className="achievements-page__stat-label">Achievements Unlocked</span>
            </div>
          </div>
        </div>

        <div className="achievements-page__filter-bar">
          {filterOptions.map(option => (
            <button
              key={option.value}
              className={`achievements-page__filter-button ${filter === option.value ? 'active' : ''}`}
              onClick={() => setFilter(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="achievements-page__grid">
          {filterAchievements(achievements).map((achievement) => (
            <AchievementCard
              key={achievement.id}
              achievement={achievement}
              isPublicView={true}
              onMarkNotified={() => {}} // No-op for public view
            />
          ))}

          {filterAchievements(achievements).length === 0 && (
            <div className="achievements-page__empty">
              <h3>No achievements found</h3>
              <p>Try a different filter to see more achievements!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicAchievementsPage;